import { GlobalContext } from "../GlobalContext";

type OptionalGenericArrayType<T> = T[] | undefined | null;

export class ArrayUtils {
    /**
     * Presune zaznam z pozice from na pozici to
     */
    public static move(array: any[], from: number, to: number): any[] {
        if (from == to) return array;

        let item = array.splice(from, 1);

        if (item.length)
            item = item[0]; //pravdepodobne muze nastat situace ze se do itemu dostane prvek typu pole, misto daneho prvku

        array.splice(to, 0, item);

        return array;
    }

    /**
     * Nalezne společné prvky dvou polí, které pak vrátí jako nové pole
     * @param array1 Pole 1
     * @param array2 Pole 2
     * @param comparator Funkce pro porovnání rovnosti
     * @returns Pole se společnými prvky
     */
    public static intersect<T>(array1: OptionalGenericArrayType<T>, array2: OptionalGenericArrayType<T>, comparator?: (item1: T, item2: T) => boolean): T[] {
        if (!array1 || !array2)
            return [];
        if (comparator)
            return array1.filter(item1 => array2.some(item2 => comparator(item1, item2)));
        return array1.filter(item1 => array2.includes(item1));
    }

    /**
     * Vrátí hodnoty prvního pole, které nejsou společné pro obě pole, jako nové pole
     * @param array1 Pole 1
     * @param array2 Pole 2
     * @param comparator Funkce pro porovnání rovnosti
     * @returns První pole s odstraněnými společnými prvky
     * @example
     * // a = [1,2,3,4]; b = [1,3,6];
     * // c = ArrayUtils.difference(a, b);
     * // c == [2, 4];
     */
    public static difference<T>(array1: T[] | ReadonlyArray<T>, array2: T[] | ReadonlyArray<T>, comparator?: (item1: T, item2: T) => boolean): T[] {
        if (comparator) {
            return array1
                .filter(item1 => !array2.some(item2 => comparator(item1, item2)));
        }
        return array1.filter(item1 => !array2.includes(item1));
    }

    /**
     * Nalezne rozdílné prvky dvou polí, které pak vrátí jako nové pole
     * @param array1 Pole 1
     * @param array2 Pole 2
     * @param comparator Funkce pro porovnání rovnosti
     * @returns Pole se společnými prvky
     * @example
     * // a = [1,2,3,4]; b = [1,3,6];
     * // c = ArrayUtils.differenceSymmetrical(a, b);
     * // c == [2, 4, 6];
     */
    public static differenceSymmetrical<T>(array1: T[] | ReadonlyArray<T>, array2: T[] | ReadonlyArray<T>, comparator?: (item1: T, item2: T) => boolean): T[] {
        if (comparator) {
            return array1
                .filter(item1 => !array2.some(item2 => comparator(item1, item2)))
                .concat(array2.filter(item1 => !array2.some(item2 => comparator(item1, item2))));
        }
        return array1.filter(x => !array2.includes(x)).concat(array2.filter(x => !array1.includes(x)));
    }

    /**
     * Nalezne zda existuje průnik dvou polí
     * @param array1 Pole 1
     * @param array2 Pole 2
     * @param comparator Funkce pro porovnání rovnosti
     */
    public static hasIntersection<T>(array1: T[] | ReadonlyArray<T>, array2: T[] | ReadonlyArray<T>, comparator?: (item1: T, item2: T) => boolean): boolean {
        if (comparator) return array1.some(item1 => array2.some(item2 => comparator(item1, item2)));
        return array1.some(item => array2.includes(item));
    }

    /**
     * Nalezne zda existuje průnik dvou kolekcí
     * @param array1 Pole 1
     * @param array2 Set 2
     * @param adapter Funkce převádějící prvek prvního typu na prvek druhého typu
     */
    public static hasIntersectionWithSet<T1, T2>(array1: T1[] | ReadonlyArray<T1>, array2: Set<T2>, adapter: (item1: T1) => T2): boolean {
        return array1.some(item1 => array2.has(adapter(item1)));
    }

    /**
     * Z existujícího pole odstraní daný prvek a to existujici pole vrati
     * @param array Pole s daty
     * @param itemForRemoval Prvek k odstranění
     * @param firstOccurrence Výchozí true, odstraní pouze první výskyt prvku
     */
    public static remove<T>(array: T[], itemForRemoval: T, firstOccurrence: boolean = true): T[] {
        let occur = array.indexOf(itemForRemoval);

        while (occur != -1) {
            array.splice(occur, 1);
            occur = array.indexOf(itemForRemoval);
            if (firstOccurrence) break;
        }

        return array;
    }

    /**
     * Vytvoří nové pole bez prvků, které neodpovídají predikátu
     * @param array Pole s daty
     * @param predicate Predikát určující, zda se má prvek odstranit
     */
    public static removeIf<T>(array: T[], predicate: (item: T) => boolean): T[] {
        return array.filter(item => !predicate(item));
    }

    /**
     * Vloží nový záznam před záznam na dané pozici. Modifikuje předané pole. A predane pole vrati
     */
    public static insertBefore<T>(array: T[], data: T, index: number): T[] {
        array.splice(index, 0, data);

        return array;
    }

    /**
     * Vyhleda index hodnoty v setridenem poli
     */
    public static binaryIndexOf(array: any[] | ReadonlyArray<any>, search: any): number {
        let minIndex = 0;
        let maxIndex = array.length - 1;
        let currIdx: number;
        let currEl: any;

        while (minIndex <= maxIndex) {
            currIdx = (minIndex + maxIndex) / 2 | 0;
            currEl = array[currIdx];

            if (currEl < search) {
                minIndex = currIdx + 1;
            } else if (currEl > search) {
                maxIndex = currIdx - 1;
            } else {
                return currIdx;
            }
        }
        return -1;
    }

    /**
     * Porovná 2 pole na základě hodnot
     * @param array1 První pole
     * @param array2 Druhé pole
     * @param sort Zda obě pole před porovnáním setřídit
     */
    public static equals(array1: Array<any>, array2: Array<any>, sort: boolean = false): boolean {
        if (!array1 || !array2) {
            return false;
        }

        if (array1.length !== array2.length) {
            return false;
        }

        if (sort) {
            array1 = array1.sort();
            array2 = array2.sort();
        }

        for (let i = 0, l = array1.length; i < l; i++) {
            if (array1[i] instanceof Array && array2[i] instanceof Array) {
                if (!ArrayUtils.equals(array1[i], array2[i])) {
                    return false;
                }
            } else if (array1[i] !== array2[i]) {
                // Doplnit porovnani objektu - dve ruzne instance objektu nejsou stejne: {x:20} != {x:20}
                return false;
            }
        }
        return true;
    }

    /**
     * Vrátí nové pole obsahující každý prvek předaného pole maximálně jednou
     * @param array Pole
     */
    public static getUniqueArray<T>(array: T[] | ReadonlyArray<T>): T[] {
        const u: any = {};
        const a: T[] = [];
        for (let i = 0, l = array.length; i < l; ++i) {
            if (u[array[i]] == 1) continue;

            a.push(array[i]);
            u[array[i]] = 1;
        }
        return a;
    }

    public static copyUint8Array(data: Uint8Array, start: number = 0, length?: number): Uint8Array {
        if (!length)
            length = data.length - start;
        const result = new Uint8Array(length);
        let j = 0;
        for (let i = start; i < length; i++)
            result[j++] = data[i];
        return result;
    }

    /**
     * Ze zdrojového pole odstraní všechny prvky, které jsou v druhém poli
     * @param sourceArray Zdrojové pole
     * @param arrayToRemove Pole s prvky k odstranění
     * @return Pole bez odstraněných prvků
     */
    public static removeAll<T>(sourceArray: T[], arrayToRemove: T[]): T[] {
        return sourceArray.filter(item => !arrayToRemove.includes(item));
    }

    /**
     * Seskupí prvky pole do skupin podle vlastnosti prvku pole definované jako klíč
     * @param data Zdrojové pole
     * @param key Vlastnost prvků pole, podle které se mají seskupit
     * @see https://learnwithparam.com/blog/how-to-group-by-array-of-objects-using-a-key/
     */
    public static groupBy<T>(data: T[], key: keyof T): {[key: string]: T[]} {
        return data.reduce((groupedItems, item) => {
            const itemValue = item[key];
            if (groupedItems[itemValue] === undefined)
                groupedItems[itemValue] = [];
            groupedItems[itemValue].push(item);
            return groupedItems;
        }, {} as any);
    }

    /**
     * Sestavi string z charCode bufferu
     * @param bytes Zdrojove pole
     */
    public static bufferToString(bytes: ArrayBuffer): string {
        const bufView = new Uint16Array(bytes);
        const length = bufView.length;
        let result = "";
        let addition = Math.pow(2, 14) - 1; // Edge fails for 2^14 and larger

        for (let i = 0; i < length; i += addition) {
            if (i + addition > length)
                addition = length - i;

            result += String.fromCharCode.apply(null, bufView.subarray(i, i + addition) as any as number[]);
        }

        return result;
    }

    /**
     * Na základě splnění predikátu rozdělí vstupní pole na dvě. Prvky, které splnily predikát se vrací v poli jako návratová hodnota.
     * Prvky které nevyhověly se vrací v parametrem předaném poli.
     * @param array Vstupní pole
     * @param predicate Predikát
     * @param omittedItems Pole prvků, které nevyhověly predikátu
     */
    public static filterSplit<T>(array: T[], predicate: (value: T, index: number, array: T[]) => boolean, omittedItems: T[]): T[] {
        const passingItems: T[] = [];

        let item: T | null = null;
        for (let i = 0; i < array.length; ++i) {
            item = array[i];

            if (predicate(item, i, array)) {
                passingItems.push(item);
            } else {
                omittedItems.push(item);
            }
        }

        return passingItems;
    }

    /**
     * Zploští pole o jednu úroveň. Vrací nové pole, původní pole nechá nezměněné.
     * Pokud vstupní pole obsahuje prvky, které nejsou typu pole, tak je vloží, tak jak jsou.
     * @param array Pole polí stejného typu.
     */
    public static flat<T>(array: ReadonlyArray<T | ReadonlyArray<T>> | null | undefined): T[] {
        return ([] as T[]).concat(...array ?? []);
    }
}

GlobalContext.registerType(ArrayUtils);
